import {onSnapshot, Query, DocumentReference, DocumentSnapshot } from "firebase/firestore";
import {useEffect, useState} from "react";

// NOTE: these are rough and naive. If they're used in very many different places, they'll likely overfetch data
// because there's no caching or consolidation. As long as they're mainly used from the context providers it should
// be fine. But I suspect we'll need to swap in something like web or onboarding use eventually.

export function useSubscribedList<DocType, DataKey extends string = "data">(
  queryOrGetter: Query<DocType> | (()=>Promise<Query<DocType>>) | undefined,
  dataKey: DataKey = "data" as DataKey
): { [K in DataKey]: DocType[] } & { loading: boolean; error: string | null } {
  const [data, setData] = useState<DocType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!queryOrGetter) {
      setData([]);
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      try {
        const query = typeof queryOrGetter === "function" ? await queryOrGetter() : queryOrGetter;
        unsubscribe = onSnapshot(query, (snapshot) => {
          setData(snapshot.docs.map((doc) => doc.data()));
          setError(null);
          setLoading(false);
        }, (err) => {
          // Firestore terminates a listener permanently on a snapshot error (a
          // permission denial, most often) rather than retrying it. Without this
          // handler the failure escaped the try/catch entirely and the caller sat
          // on loading forever with a null error.
          console.error('Error subscribing ', dataKey, err);
          setError(`Failed to load ${dataKey}`);
          setLoading(false);
        });
      } catch (err) {
        console.error('Error subscribing ', dataKey, err);
        setError(`Failed to load ${dataKey}`);
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [queryOrGetter, dataKey]);

  return { [dataKey]: data ?? [], loading, error } as { [K in DataKey]: DocType[] } & { loading: boolean; error: string | null };
}

export function useSubscribedDoc<DocType, DataKey extends string = "data">(
  docRefOrGetter: DocumentReference<DocType> | (()=>Promise<DocumentReference<DocType>>) | undefined,
  dataKey: DataKey = "data" as DataKey
): { [K in DataKey]: DocType | null } & { loading: boolean; error: string | null } {
  const [data, setData] = useState<DocType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!docRefOrGetter) {
      setData(null);
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      try {
        const docRef = typeof docRefOrGetter === "function" ? await docRefOrGetter() : docRefOrGetter;
        unsubscribe = onSnapshot(docRef, (snapshot: DocumentSnapshot<DocType>) => {
          setData(snapshot.data() ?? null);
          setError(null);
          setLoading(false);
        }, (err) => {
          // See useSubscribedList: snapshot errors kill the listener for good, so
          // they have to be surfaced here rather than left to the try/catch.
          console.error('Error subscribing ', dataKey, err);
          setError(`Failed to load ${dataKey}`);
          setLoading(false);
        });
      } catch (err) {
        console.error('Error subscribing ', dataKey, err);
        setError(`Failed to load ${dataKey}`);
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [docRefOrGetter, dataKey]);

  return { [dataKey]: data ?? null, loading, error } as { [K in DataKey]: DocType | null } & { loading: boolean; error: string | null };
}

